// Frontend\src\component\hero\HeroSection.js
import React, { useEffect, useState } from "react";
import { Button } from "@mui/material";
import { motion } from "framer-motion";
import botAnime from "../../asset/botAnime2.mp4";
import styles from "./HeroSection.module.css";

// Binary data particles for background
const BinaryParticleBackground = () => {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const generateParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 40; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 3 + 1,
          speed: Math.random() * 0.5 + 0.2,
          content: Math.random() > 0.5 ? "1" : "0",
          opacity: Math.random() * 0.3 + 0.1,
        });
      }
      setParticles(newParticles);
    };
    generateParticles();
  }, []);

  return (
    <div className={styles.binaryBackground}>
      {particles.map((particle) => (
        <div
          key={particle.id}
          className={styles.binaryParticle}
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            fontSize: `${particle.size}rem`,
            animationDelay: `${particle.speed}s`,
            opacity: particle.opacity,
          }}
        >
          {particle.content}
        </div>
      ))}
    </div>
  );
};

const HeroSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if ("ontouchstart" in window) return;

    const { innerWidth, innerHeight } = window;
    const x = (e.clientX / innerWidth - 0.5) * 10;
    const y = (e.clientY / innerHeight - 0.5) * 10;

    // Store mouse position for dynamic lighting
    setMousePosition({ x: e.clientX, y: e.clientY });

    // Parallax effect for elements
    // document.documentElement.style.setProperty(
    //   "--grid-x",
    //   `${e.clientX * 0.05}px`
    // );
    // document.documentElement.style.setProperty(
    //   "--grid-y",
    //   `${e.clientY * 0.05}px`
    // );

    // e.currentTarget.style.transform = `
    //   perspective(1200px)
    //   rotateY(${x}deg)
    //   rotateX(${-y}deg)
    //   translateZ(20px)
    // `;
  };

  // const handleMouseLeave = (e) => {
  //   e.currentTarget.style.transform = `
  //     perspective(1200px)
  //     rotateX(0deg)
  //     rotateY(0deg)
  //     translateZ(0px)
  //   `;
  // };

  return (
    <section
      id="home"
      className={`min-h-screen w-full px-4 sm:px-8 relative overflow-hidden `}
      onMouseMove={handleMouseMove}
    >
      {/* Dynamic gradient background based on mouse position */}
      <div 
        className={styles.dynamicGradient}
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, 
            rgba(0, 212, 255, 0.15) 0%, 
            rgba(9, 9, 121, 0.1) 25%, 
            rgba(2, 0, 36, 0.05) 50%, 
            transparent 70%)`
        }}
      />

      {/* Binary data particle background */}
      <BinaryParticleBackground />

      {/* Animated circuit lines */}
      <div className={styles.circuitLines}></div>

      {/* Enhanced AI Orbs with glow */}
      <div className={styles.orbContainer}>
        <span className={`${styles.orb} ${styles.orb1}`}></span>
        <span className={`${styles.orb} ${styles.orb2}`}></span>
        <span className={`${styles.orb} ${styles.orb3}`}></span>
        {/* Additional orbs for more tech feel */}
        <span className={`${styles.orb} ${styles.orb4}`}></span>
        <span className={`${styles.orb} ${styles.orb5}`}></span>
      </div>

      {/* Animated grid */}
      <div className={styles.neonGrid}></div>

      {/* Scan line effect */}
      {/* <div className={styles.scanLine}></div> */}

      {/* Main Content */}
      <div
        className={`${styles.heroInner} flex flex-col-reverse md:flex-row items-center justify-between gap-10 md:gap-24 lg:gap-32 mx-auto relative z-20 transition-transform duration-200 ease-out`}
        // onMouseMove={handleMouseMove}
        // onMouseLeave={handleMouseLeave}
      >
        {/* Left Section */}
        <motion.div
          initial={{ x: "-100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="w-full md:w-auto md:max-w-[55%] lg:max-w-[60%] text-center md:text-left relative z-20"
        >
          {/* Tech badge */}
          <div className={styles.techBadge}>
            <span className="text-xs font-bold tracking-widest uppercase">
              NEXT-GEN AI PLATFORM
            </span>
          </div>

          <h1
            className={`${styles.gradientText} text-4xl sm:text-5xl md:text-6xl xl:text-7xl lg:text-7xl font-bold leading-tight mb-6`}
          >
            WELCOME TO <br /> 
            <span className={styles.aiTextGlow}>AI-MEBOT</span>
          </h1>

          <p className={`${styles.techText} text-base sm:text-lg md:text-xl lg:text-2xl max-w-xl mb-8`}>
            Discover Limitless Possibilities: Meet MeBot, Your Smart AI Assistant!
            <span className={styles.textCursor}>|</span>
          </p>

          {/* Feature highlights */}
          {/* <div className={`${styles.featureGrid} mb-8`}>
            <div className={styles.featureItem}>
              <span className={styles.featureIcon}>⚡</span>
              <span>Real-time Processing</span>
            </div>
            <div className={styles.featureItem}>
              <span className={styles.featureIcon}>🤖</span>
              <span>Neural Networks</span>
            </div>
            <div className={styles.featureItem}>
              <span className={styles.featureIcon}>🔒</span>
              <span>Secure API</span>
            </div>
          </div> */}

          <div className="flex flex-col sm:flex-row items-center gap-4 w-full">
            {/* Enhanced Email Input */}
            <div className={styles.inputContainer}>
              <input
                type="email"
                placeholder="Enter your email"
                className={`${styles.techInput} w-full sm:w-72 px-4 py-3`}
              />
              <span className={styles.inputGlow}></span>
            </div>

            {/* Enhanced Request Demo Button */}
            <motion.div 
              whileHover={{ scale: 1.05 }} 
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <Button
                variant="contained"
                className={styles.techButton}
                sx={{
                  background: "linear-gradient(90deg, #00d4ff 0%, #00b4ff 50%, #0094ff 100%)",
                  color: "#000000",
                  "&:hover": {
                    background: "linear-gradient(90deg, #00e4ff 0%, #00c4ff 50%, #00a4ff 100%)",
                    boxShadow: "0 0 10px rgba(0, 212, 255, 0.8)",
                  },
                  px: 4,
                  py: 1.8,
                  textTransform: "none",
                  fontWeight: 700,
                  fontFamily: "'Raleway','Poppins', sans-serif",
                  borderRadius: "12px",
                  position: "relative",
                  overflow: "hidden",
                  border: "2px solid rgba(0, 212, 255, 0.5)",
                }}
              >
                <span className={styles.buttonText}>Request Demo</span>
              </Button>
            </motion.div>
          </div>
        </motion.div>

        {/* Right Section - Enhanced Video Container */}
        <motion.div
          initial={{ x: "100%", opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="w-full md:w-1/2 flex justify-center mb-10 md:mb-0 relative z-20"
        >
          <div className={styles.videoContainer}>
            {/* Circuit border */}
            <div className={styles.circuitBorder}>
              {[...Array(8)].map((_, i) => (
                <div key={i} className={styles.cornerDot}></div>
              ))}
            </div>
            
            {/* Neon rings */}
            <div className={styles.neonRing}></div>
            <div className={styles.neonRing2}></div>
            
            {/* Holographic glow */}
            <div className={styles.holographicGlow}></div>
            
            {/* Video with enhanced effects */}
            <video
              src={botAnime}
              autoPlay
              loop
              muted
              playsInline
              className={styles.heroVideo}
            />
            
            {/* Floating data points */}
            <div className={styles.dataPoints}>
              {[...Array(6)].map((_, i) => (
                <div key={i} className={styles.dataPoint}></div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Bottom tech elements */}
      <div className={styles.bottomTech}>
        <div className={styles.connectorDots}>
        
        </div>
      </div>
    </section>
  );
};

export default HeroSection;